// src/features/property-templates/templatesThunks.ts
import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type { CreateItemResponse } from '@/services/api/types';
import { selectIsSuperAdmin } from '@/features/auth/authSlice';

import type { PropertyTemplate, TemplateAudience, TemplateStatus } from '@/domain/entities/preperty-template';
import type {
  CreateTemplatePayload,
  DeleteTemplateResponse,
  ListTemplatesResponse,
  UpdateTemplatePayload,
} from './propertyTemplatesTypes';

// ---- API payload/response types (keep these next to templates, like schemasThunkTypes) ----

// ---- thunks ----

export const fetchPropertyTemplates = createAppAsyncThunk<
  ListTemplatesResponse,
  { status: TemplateStatus | 'all'; orgID?: string; audience?: TemplateAudience | 'all' }
>(
  'propertyTemplates/fetchByStatus',
  async ({ status, orgID, audience = 'all' }, { extra: { config }, getState, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // optional: simulate latency in dev
      // await sleep(800);
      const isSuperAdmin = selectIsSuperAdmin(getState());
      const normalizedOrgID = orgID?.trim();
      if (!isSuperAdmin && !normalizedOrgID) {
        return rejectWithValue({ message: 'orgID is required to list templates for non-super-admin users.' });
      }

      const params = new URLSearchParams();
      params.set('status', status);
      params.set('audience', audience);
      if (normalizedOrgID) params.set('orgID', normalizedOrgID);

      const endpoint = `${getApiEndpoints().core.propertyTemplates.listTemplates}?${params.toString()}`;
      return await apiService.get<ListTemplatesResponse>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const fetchTemplateById = createAppAsyncThunk<PropertyTemplate, { id: string }>(
  'propertyTemplates/fetchById',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.propertyTemplates.getTemplate}?id=${encodeURIComponent(id)}`;
      return await apiService.get<PropertyTemplate>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const createPropertyTemplate = createAppAsyncThunk<CreateItemResponse<PropertyTemplate>, CreateTemplatePayload>(
  'propertyTemplates/create',
  async (input, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertyTemplates.createTemplate;
      return await apiService.put<CreateItemResponse<PropertyTemplate>>(endpoint, input);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const updatePropertyTemplate = createAppAsyncThunk<CreateItemResponse<PropertyTemplate>, UpdateTemplatePayload>(
  'propertyTemplates/update',
  async (input, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = getApiEndpoints().core.propertyTemplates.updateTemplate + `?id=${encodeURIComponent(input.id)}`;
      return await apiService.put<CreateItemResponse<PropertyTemplate>>(endpoint, input);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const deleteTemplate = createAppAsyncThunk<DeleteTemplateResponse, { id: string }>(
  'propertyTemplates/delete',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      // optional: simulate latency
      // await sleep(600);

      const endpoint = `${getApiEndpoints().core.propertyTemplates.deleteTemplate}?id=${encodeURIComponent(id)}`;
      return await apiService.delete<DeleteTemplateResponse>(endpoint);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message || 'Failed to delete template' });
    }
  },
);

/**
 * Optional lifecycle endpoints (only keep if you have them)
 * - publishTemplate: moves draft -> published
 * - deprecateTemplate: published -> deprecated
 * - cloneTemplate: clone published into new draft
 */

export const publishTemplate = createAppAsyncThunk<void, { id: string }>(
  'propertyTemplates/publish',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.propertyTemplates.publishTemplate}?id=${encodeURIComponent(id)}`;
      await apiService.post(endpoint, null);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const deprecateTemplate = createAppAsyncThunk<void, { id: string }>(
  'propertyTemplates/deprecate',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.propertyTemplates.deprecateTemplate}?id=${encodeURIComponent(id)}`;
      await apiService.post(endpoint, null);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);

export const cloneTemplate = createAppAsyncThunk<CreateItemResponse<PropertyTemplate>, { id: string }>(
  'propertyTemplates/clone',
  async ({ id }, { extra: { config }, rejectWithValue }) => {
    const { apiService } = config;

    try {
      const endpoint = `${getApiEndpoints().core.propertyTemplates.cloneTemplate}?id=${encodeURIComponent(id)}`;
      return await apiService.post<CreateItemResponse<PropertyTemplate>>(endpoint, undefined);
    } catch (err) {
      const e = err as Error & { status?: number; body?: string };
      return rejectWithValue({ message: e.message });
    }
  },
);
